<template>
  <v-card class="detail-card" elevation="1">
    <div class="detail-card__header">
      <div class="detail-card__identity">
        <v-avatar class="detail-card__avatar" size="52">
          <v-icon color="teal darken-2" size="24">mdi-waves</v-icon>
        </v-avatar>
        <div>
          <div class="detail-card__eyebrow">{{ isCaudal ? 'Remote' : 'Contador' }}</div>
          <div class="detail-card__title">{{ title }}</div>
          <div class="detail-card__subtitle">{{ subtitle }}</div>
        </div>
      </div>

      <div class="detail-card__actions">
        <div v-if="batteryInfo" class="battery-chip" :class="`battery-chip--${batteryInfo.color}`">
          <v-icon size="17">{{ batteryInfo.icon }}</v-icon>
          <span>{{ batteryInfo.label }}</span>
        </div>
        <v-btn icon small text @click="clearContador">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="detail-card__stats" :class="isCaudal ? 'detail-card__stats--4col' : ''">
      <div v-for="stat in stats" :key="stat.label" class="detail-stat">
        <span class="detail-stat__label">{{ stat.label }}</span>
        <strong>{{ stat.value }}</strong>
      </div>
    </div>

    <v-card-text class="detail-card__chart">
      <div v-if="noData" class="no-data">
        <v-icon size="40" color="red lighten-2">mdi-water-remove-outline</v-icon>
        <span>No se han encontrado datos</span>
      </div>
      <template v-else>
        <apexchart ref="chartRef" :key="item.id" :height="chartHeight" :options="chartOptions" :series="series" />
        <div v-if="isCaudal && legendItems.length" class="chart-legend">
          <template v-for="(legendItem, i) in legendItems">
            <span v-if="i > 0" :key="`sep-${legendItem.name}`" class="chart-legend__sep">|</span>
            <span
              :key="legendItem.name"
              class="chart-legend__item"
              :class="{ 'chart-legend__item--hidden': hiddenSeries.includes(legendItem.name) }"
              @click="toggleSeries(legendItem.name)"
            >
              <span class="chart-legend__dot" :style="{ background: legendItem.color }" />
              {{ legendItem.name }}
            </span>
          </template>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';

const MOBILE_BREAKPOINT = 760;

const CAUDAL_COLORS = {
  'Caudal instantáneo': '#00796B',
  'Caudal acumulado':   '#FFB300',
  'Caudal negativo':    '#ef4444',
  'Bruto':              '#06b6d4',
};

const BATERIA_CONTADOR_BREAKPOINTS = [
  { v: 3.00, pct: 0   }, { v: 3.05, pct: 8   }, { v: 3.10, pct: 17  },
  { v: 3.15, pct: 25  }, { v: 3.20, pct: 33  }, { v: 3.25, pct: 42  },
  { v: 3.30, pct: 50  }, { v: 3.35, pct: 58  }, { v: 3.40, pct: 67  },
  { v: 3.45, pct: 75  }, { v: 3.50, pct: 83  }, { v: 3.55, pct: 92  },
  { v: 3.60, pct: 100 },
];

function voltajeBateriaContadorAgua(voltaje) {
  if (voltaje <= 3.00) return 0;
  if (voltaje >= 3.60) return 100;
  let lo = null;
  for (let i = BATERIA_CONTADOR_BREAKPOINTS.length - 1; i >= 0; i--) {
    if (BATERIA_CONTADOR_BREAKPOINTS[i].v <= voltaje) { lo = BATERIA_CONTADOR_BREAKPOINTS[i]; break; }
  }
  const hi = BATERIA_CONTADOR_BREAKPOINTS.find((b) => b.v > voltaje);
  if (!lo || !hi) return 0;
  const t = (voltaje - lo.v) / (hi.v - lo.v);
  return Math.round(lo.pct + t * (hi.pct - lo.pct));
}

const fmtUnit = (u) => String(u ?? '').replace(/m3/g, 'm³');

function formatMetric(value, decimals, unit) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'Sin dato';
  const u = fmtUnit(unit);
  return u ? `${parsed.toFixed(decimals)} ${u}`.trim() : parsed.toFixed(decimals);
}

function buildCounterOptions(isMobile) {
  return {
    chart: {
      id: 'area-datetime',
      type: 'area',
      zoom: { enabled: true, type: 'x' },
      toolbar: {
        show: !isMobile,
        tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      },
      animations: { enabled: false },
      background: 'transparent',
      fontFamily: 'inherit',
    },
    colors: ['#00897b', '#f97316'],
    fill: {
      type: 'gradient',
      gradient: { type: 'vertical', shadeIntensity: 0.5, opacityFrom: 0.55, opacityTo: 0.03, stops: [0, 85, 100] },
    },
    stroke: { curve: 'smooth', width: 2.5, lineCap: 'round' },
    grid: {
      borderColor: '#f0f4f8',
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 4, right: isMobile ? 4 : 12, bottom: 0, left: isMobile ? 4 : 12 },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false,
        format: 'dd/MM HH:mm',
        hideOverlappingLabels: true,
        showDuplicates: false,
        style: { fontSize: isMobile ? '10px' : '11px', colors: '#b0bec5', fontWeight: 400 },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: true, width: 1, stroke: { color: '#009688', width: 1, dashArray: 4 } },
      tooltip: {
        enabled: true,
        formatter: (value) => new Date(value).toLocaleString('es-ES', {
          year: '2-digit', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        }),
      },
    },
    yaxis: {
      min: 0,
      labels: {
        style: { colors: '#b0bec5', fontSize: isMobile ? '10px' : '11px' },
        formatter: (value) => (value != null ? Math.round(value) : ''),
      },
    },
    markers: { size: 0, hover: { size: 6, sizeOffset: 0 }, strokeColors: '#fff', strokeWidth: 2, fillOpacity: 1 },
    dataLabels: { enabled: false },
    tooltip: {
      theme: 'light',
      x: { format: 'dd/MM/yy HH:mm' },
      y: { formatter: (value) => (value != null ? `${value}` : '-') },
      marker: { show: true },
      style: { fontSize: '12px' },
    },
    noData: { text: 'Cargando datos...', style: { color: '#b0bec5', fontSize: '13px' } },
  };
}

function buildCaudalOptions(yaxis, seriesList, isMobile) {
  const colors          = seriesList.map((s) => CAUDAL_COLORS[s.name] ?? '#90a4ae');
  const fillTypes       = seriesList.map((s) => (s.type === 'area' ? 'gradient' : 'solid'));
  const fillOpacityFrom = seriesList.map((s) => (s.type === 'area' ? 0.5 : 0));
  const fillOpacityTo   = seriesList.map((s) => (s.type === 'area' ? 0.03 : 0));
  const strokeWidths    = seriesList.map((s) => (s.name === 'Bruto' ? 2 : s.type === 'line' ? 4 : 2));
  const dashArray       = seriesList.map((s) => (s.name === 'Bruto' ? 6 : 0));

  return {
    chart: {
      id: 'flow-datetime',
      type: 'line',
      zoom: { enabled: true, type: 'x' },
      toolbar: {
        show: !isMobile,
        tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true },
      },
      animations: { enabled: false },
      background: 'transparent',
      fontFamily: 'inherit',
    },
    colors,
    fill: {
      type: fillTypes,
      gradient: { type: 'vertical', shadeIntensity: 0.4, opacityFrom: fillOpacityFrom, opacityTo: fillOpacityTo, stops: [0, 85, 100] },
    },
    stroke: { width: strokeWidths, curve: 'smooth', lineCap: 'round', dashArray },
    grid: {
      borderColor: '#f0f4f8',
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 4, right: isMobile ? 4 : 12, bottom: 0, left: isMobile ? 4 : 12 },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false,
        format: 'dd/MM HH:mm',
        hideOverlappingLabels: true,
        showDuplicates: false,
        style: { fontSize: isMobile ? '10px' : '11px', colors: '#b0bec5', fontWeight: 400 },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: true, width: 1, stroke: { color: '#90a4ae', width: 1, dashArray: 4 } },
      tooltip: {
        enabled: true,
        formatter: (value) => new Date(value).toLocaleString('es-ES', {
          day: '2-digit', month: '2-digit', year: '2-digit',
          hour: '2-digit', minute: '2-digit', hour12: false,
        }),
      },
    },
    yaxis,
    markers: { size: 0, hover: { size: 5 }, strokeColors: '#fff', strokeWidth: 2, fillOpacity: 0.85 },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: {
      x: { format: 'dd/MM/yy HH:mm' },
      shared: true,
      intersect: false,
      theme: 'light',
      marker: { show: true },
      style: { fontSize: '12px' },
      y: { formatter: (value) => (value != null ? Number(value).toFixed(2) : '-') },
    },
    noData: { text: 'Cargando datos...', style: { color: '#b0bec5', fontSize: '13px' } },
  };
}

export default {
  name: 'ContadoresChart',
  components: { apexchart: VueApexCharts },
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      noData: false,
      series: [],
      chartOptions: buildCounterOptions(window.innerWidth <= MOBILE_BREAKPOINT),
      isMobile: window.innerWidth <= MOBILE_BREAKPOINT,
      hiddenSeries: [],
    };
  },
  computed: {
    isCaudal() {
      return this.item?.raw?.kind === 'caudal';
    },
    contadorData() {
      return this.item?.raw?.data ?? null;
    },
    sensors() {
      return this.item?.raw?.sensors ?? [];
    },
    chartHeight() {
      return this.isMobile ? 270 : 340;
    },
    sensorBateria() {
      return this.sensors.find((s) => Number(s.id_tipo_sensor) === 25) ?? null;
    },
    sensorInst() {
      return this.sensors.find((s) => Number(s.id_tipo_sensor) === 26) ?? null;
    },
    sensorAcum() {
      return this.sensors.find((s) => Number(s.id_tipo_sensor) === 27) ?? null;
    },
    sensorAcumNeg() {
      return this.sensors.find((s) => Number(s.id_tipo_sensor) === 28) ?? null;
    },
    sensorHoras() {
      return this.sensors.find((s) => Number(s.id_tipo_sensor) === 30) ?? null;
    },
    title() {
      return this.item?.nombre ?? 'Contador';
    },
    subtitle() {
      if (this.isCaudal) {
        const remote = this.item?.raw?.remoteId ?? 'Sin remote';
        const count = this.sensors.length;
        return `${remote} · ${count} ${count === 1 ? 'sensor' : 'sensores'}`;
      }
      return this.contadorData?.id_remote ?? 'Sin remote asignado';
    },
    batteryInfo() {
      if (!this.isCaudal) return null;
      const sensor = this.sensorBateria;
      if (!sensor) return null;
      const voltage = Number(sensor.value);
      if (!Number.isFinite(voltage)) return null;
      const pct = voltajeBateriaContadorAgua(voltage);
      let icon = 'mdi-battery';
      let color = 'success';
      if (pct <= 10) { icon = 'mdi-battery-alert'; color = 'error'; }
      else if (pct <= 25) { icon = 'mdi-battery-low'; color = 'error'; }
      else if (pct <= 50) { icon = 'mdi-battery-medium'; color = 'warning'; }
      else if (pct <= 75) { icon = 'mdi-battery-60'; color = 'success'; }
      return { pct, icon, color, label: `${pct.toFixed(0)}%` };
    },
    stats() {
      if (this.isCaudal) {
        const horas = Number(this.sensorHoras?.value);
        return [
          { label: 'Instantáneo',      value: formatMetric(this.sensorInst?.value,    2, this.sensorInst?.unidades) },
          { label: 'Acumulado',         value: formatMetric(this.sensorAcum?.value,    2, this.sensorAcum?.unidades) },
          { label: 'C. Negativo',       value: formatMetric(this.sensorAcumNeg?.value, 2, this.sensorAcumNeg?.unidades) },
          { label: 'D. Funcionamiento', value: Number.isFinite(horas) ? `${(horas / 24).toFixed(1)} días` : 'Sin dato' },
        ];
      }
      const fechaRaw = this.contadorData?.fecha_registro;
      const fechaLabel = fechaRaw
        ? new Date(fechaRaw).toLocaleString('es-ES', {
            day: '2-digit', month: '2-digit', year: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false,
          })
        : 'Sin dato';
      return [
        { label: 'Caudal acumulado',   value: String(Number(this.contadorData?.n_pulsos ?? 0)) },
        { label: 'Caudal instantáneo', value: formatMetric(this.contadorData?.caudal, 2, 'm³/h') },
        { label: 'Última lectura',     value: fechaLabel },
      ];
    },
    legendItems() {
      return this.series
        .map((s) => ({ name: s.name, color: CAUDAL_COLORS[s.name] }))
        .filter((li) => li.color);
    },
  },
  watch: {
    item: {
      immediate: true,
      deep: true,
      handler() { this.updateChart(); },
    },
    isMobile() { this.updateChart(); },
  },
  mounted() {
    this._onResize = () => { this.isMobile = window.innerWidth <= MOBILE_BREAKPOINT; };
    window.addEventListener('resize', this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._onResize);
  },
  methods: {
    buildCaudalSeries(sensor, name, chartType) {
      if (!sensor?.historico?.fecha_registro || !sensor?.historico?.values) return null;
      const values = sensor.historico.values;
      if (!values.length || values.every((v) => v == null || v === -1)) return null;
      return {
        name,
        type: chartType,
        data: sensor.historico.fecha_registro.map((date, i) => ({
          x: new Date(date),
          y: values[i] === -1 ? null : values[i],
        })),
      };
    },

    buildBrutoSeries(sensorPos, sensorNeg) {
      if (!sensorPos?.historico?.fecha_registro || !sensorNeg?.historico?.fecha_registro) return null;
      const negMap = {};
      sensorNeg.historico.fecha_registro.forEach((date, i) => { negMap[date] = sensorNeg.historico.values[i]; });
      const data = sensorPos.historico.fecha_registro
        .map((date, i) => {
          const pos = sensorPos.historico.values[i];
          if (pos == null || pos === -1) return null;
          const neg = negMap[date];
          const negVal = (neg == null || neg === -1) ? 0 : neg;
          return { x: new Date(date), y: Math.max(0, pos - negVal) };
        })
        .filter(Boolean);
      if (!data.length || data.every((d) => d.y === 0)) return null;
      return { name: 'Bruto', type: 'line', data };
    },

    updateCounterChart() {
      const historial = this.contadorData?.historico;
      const dates  = historial?.fecha_registro ?? [];
      const pulses = historial?.n_pulsos ?? [];

      this.chartOptions = buildCounterOptions(this.isMobile);

      if (!dates.length || !pulses.length) { this.noData = true; this.series = []; return; }

      this.noData = pulses.every((p) => p === 0 || p === null);
      if (this.noData) { this.series = []; return; }

      const caudalData = historial?.caudal ?? [];
      const newSeries = [
        { name: 'Caudal acumulado', data: dates.map((date, i) => ({ x: new Date(date), y: pulses[i] })) },
      ];
      if (caudalData.length && caudalData.some((v) => v != null && v !== 0)) {
        newSeries.push({
          name: 'Caudal instantáneo',
          data: dates.map((date, i) => ({ x: new Date(date), y: caudalData[i] })),
        });
      }
      this.series = newSeries;
    },

    updateCaudalChart() {
      const newSeries = [];
      const yaxis = [];

      const seriesInst    = this.buildCaudalSeries(this.sensorInst,    'Caudal instantáneo', 'area');
      const seriesAcum    = this.buildCaudalSeries(this.sensorAcum,    'Caudal acumulado',   'line');
      const seriesAcumNeg = this.buildCaudalSeries(this.sensorAcumNeg, 'Caudal negativo',    'line');
      const seriesBruto   = this.buildBrutoSeries(this.sensorAcum, this.sensorAcumNeg);

      if (seriesInst) {
        newSeries.push(seriesInst);
        yaxis.push({
          seriesName: 'Caudal instantáneo',
          opposite: true,
          title: { text: fmtUnit(this.sensorInst?.unidades) },
          labels: { formatter: (v) => (v != null ? v.toFixed(2) : '') },
          min: 0,
        });
      }
      if (seriesAcum) {
        newSeries.push(seriesAcum);
        yaxis.push({
          seriesName: 'Caudal acumulado',
          title: { text: fmtUnit(this.sensorAcum?.unidades) },
          labels: { formatter: (v) => (v != null ? v.toFixed(0) : '') },
          min: 0,
        });
      }
      if (seriesAcumNeg) {
        newSeries.push(seriesAcumNeg);
        yaxis.push({ seriesName: 'Caudal acumulado', show: false });
      }
      if (seriesBruto) {
        newSeries.push(seriesBruto);
        yaxis.push({ seriesName: seriesInst ? 'Caudal instantáneo' : 'Bruto', show: !seriesInst });
      }

      this.hiddenSeries = [];
      this.chartOptions = buildCaudalOptions(yaxis, newSeries, this.isMobile);
      this.noData = newSeries.length === 0;
      this.series = newSeries;
    },

    updateChart() {
      if (this.isCaudal) { this.updateCaudalChart(); } else { this.updateCounterChart(); }
    },

    toggleSeries(name) {
      if (this.$refs.chartRef?.chart) {
        this.$refs.chartRef.chart.toggleSeries(name);
      }
      const idx = this.hiddenSeries.indexOf(name);
      this.hiddenSeries = idx === -1
        ? [...this.hiddenSeries, name]
        : this.hiddenSeries.filter((n) => n !== name);
    },

    clearContador() {
      this.$emit('clear-contador');
    },
  },
};
</script>

<style scoped>
.detail-card {
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(255, 255, 255, 0.96);
}

.detail-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 20px 16px;
}

.detail-card__identity {
  display: flex;
  align-items: center;
  gap: 14px;
}

.detail-card__avatar {
  background: rgba(240, 253, 250, 0.94) !important;
}

.detail-card__eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.detail-card__title {
  margin-top: 4px;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: #0f766e;
}

.detail-card__subtitle {
  margin-top: 6px;
  color: #64748b;
  font-size: 14px;
}

.detail-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.battery-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px 3px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  background: #fff;
  border: 1px solid;
  pointer-events: none;
  user-select: none;
}

.battery-chip--success { border-color: #0f766e; color: #0f766e; }
.battery-chip--warning { border-color: #92400e; color: #92400e; }
.battery-chip--error   { border-color: #b91c1c; color: #b91c1c; }

.detail-card__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 0 20px 20px;
}

.detail-card__stats--4col {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.detail-stat {
  padding: 14px 16px;
  background: rgba(248, 250, 252, 0.92);
  border: 1px solid rgba(148, 163, 184, 0.14);
  border-radius: 4px;
}

.detail-stat__label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}

.detail-stat strong {
  display: block;
  margin-top: 8px;
  font-size: 20px;
  line-height: 1.1;
  color: #0f172a;
}

.detail-card__chart {
  padding: 20px !important;
}

::v-deep .apexcharts-canvas,
::v-deep .apexcharts-svg {
  max-width: 100%;
}

.chart-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding-top: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #475569;
}

.chart-legend__sep {
  color: #cbd5e1;
  font-weight: 300;
}

.chart-legend__item {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  transition: opacity 0.2s;
  user-select: none;
}

.chart-legend__item--hidden {
  opacity: 0.35;
}

.chart-legend__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 280px;
  color: #9e9e9e;
  border: 2px dashed #b2dfdb;
  gap: 12px;
  border-radius: 8px;
  animation: fadeIn 0.4s ease-in-out;
}

.no-data span {
  font-size: 14px;
  font-weight: 500;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (max-width: 760px) {
  .detail-card__header {
    padding: 16px 16px 12px;
    align-items: flex-start;
  }
  .detail-card__identity {
    flex: 1;
    min-width: 0;
    align-items: flex-start;
  }
  .detail-card__title { font-size: 20px; }
  .detail-card__subtitle { font-size: 13px; }

  .detail-card__stats,
  .detail-card__stats--4col {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    padding: 0 16px 16px;
  }

  .detail-stat { padding: 12px 14px; }
  .detail-stat strong { font-size: 18px; }

  .detail-card__chart { padding: 12px 10px 16px !important; }
  .no-data { height: 240px; }
}
</style>
